import type { Role } from '../lib/auth';
import { HELP, type Audience, type Help, type HelpDef } from './content';

export { HELP, type Help, type HelpDef } from './content';

const SHOP_ROLES: Role[] = ['shop_admin', 'shop_staff'];

/** The audiences a role belongs to, most specific first. */
function audiences(role: Role): Audience[] {
  const out: Audience[] = [role];
  if (SHOP_ROLES.includes(role)) out.push('shop');
  if (role === 'owner' || role === 'accountant') out.push('venueWide');
  return out;
}

const compiled = HELP.map((def) => ({
  def,
  // 'visits/:id' → ^visits/[^/]+$ ; every segment must match, so 'visits/:id/pass' is its own entry.
  re: new RegExp(`^${def.path.split('/').map((seg) => (seg.startsWith(':') ? '[^/]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))).join('/')}$`),
}));

/** The help entry for an address (with or without the /admin prefix, query and trailing slash ignored). */
export function helpDefFor(pathname: string): HelpDef | null {
  const rel = pathname.split(/[?#]/)[0].replace(/^\/admin\/?/, '').replace(/\/+$/, '');
  return compiled.find((c) => c.re.test(rel))?.def ?? null;
}

/**
 * The help for a page as one login reads it: the role's own text, else its group's ('shop' or 'venueWide'),
 * else the page's general text. The super admin and the accountant read shop pages as visitors.
 */
export function helpFor(pathname: string, role: Role): Help | null {
  const def = helpDefFor(pathname);
  if (!def) return null;
  const variant = audiences(role).map((a) => def.roles?.[a]).find(Boolean) ?? {};
  return { title: variant.title ?? def.title, forWhom: variant.forWhom ?? def.forWhom, lines: variant.lines ?? def.lines };
}

/** The one line a menu entry gets on "What I can do": what the page is for. */
export function summaryFor(pathname: string, role: Role): string | null {
  return helpFor(pathname, role)?.lines[0] ?? null;
}

// ------------------------------------------------------------------ first-visit hint
const SEEN_PREFIX = 'z3.help.seen.';

/** Where a login's "already seen" pages are kept on this device. */
const seenKey = (userId: string) => `${SEEN_PREFIX}${userId}`;

function readSeen(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(seenKey(userId));
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

/** Whether this login has already been shown (or dismissed) the hint for this page on this device. */
export function hintSeen(userId: string, pagePath: string): boolean {
  return readSeen(userId).has(pagePath);
}

export function markHintSeen(userId: string, pagePath: string) {
  try {
    const seen = readSeen(userId);
    if (seen.has(pagePath)) return;
    seen.add(pagePath);
    localStorage.setItem(seenKey(userId), JSON.stringify([...seen].slice(-200)));
  } catch {
    /* private mode or storage full: the hint may show again next time, nothing breaks */
  }
}

/**
 * First-visit hints are for people. Automated browsers (the end-to-end suite, the crawler) get them only when
 * a test asks for them (localStorage 'z3.help.hints' = 'on'), so the hint never sits in front of what a test
 * is looking for. The "How this works" button works the same for everyone.
 */
export function hintsEnabled(): boolean {
  try {
    const flag = localStorage.getItem('z3.help.hints');
    if (flag === 'on') return true;
    if (flag === 'off') return false;
  } catch {
    /* fall through */
  }
  return !(typeof navigator !== 'undefined' && navigator.webdriver);
}
